import { AuditFilters } from "@services/audit/types";
import {
  SLFilterToSearch,
  asNonEmptyArray,
  asBoolean,
  asString,
  asNumber,
  objectToSearchParams,
} from "./common";
import { convertLocalTime } from "@common/utils/localTime.utils";
import { TIME_FILTER_SEARCH_KEY } from "./meetings";

const getDatesForTimeFilter = (
  filter: AuditTimeFilter,
  payload?: DateInterval
) => {
  let [start, end] = [
    convertLocalTime(new Date()),
    convertLocalTime(new Date()),
  ];
  switch (filter) {
    case "Custom time period":
      let timePayload = payload as DateInterval;
      start = new Date(timePayload?.startDate);
      end = new Date(timePayload?.endDate);
      break;
    case "This month":
      start = new Date(
        new Date().getFullYear(),
        new Date().getMonth() - 1,
        new Date().getDate()
      );
      break;
    case "This week":
      start = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate() - 7
      );
      break;
    case "Today":
      start = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      );
      break;
  }
  return [start.getTime(), end.getTime()];
};
export const widgetFilterToSLFilter = (
  filters: (WidgetFilter | FilterDefinition)[] = []
): AuditFilters => {
  let mapped: AuditFilters = {};
  filters.forEach((f) => {
    switch (f.filterType as FilterTypes) {
      case "email":
        if (f.filterValue) mapped.userName = f.filterValue;
        break;
      case "action":
        mapped.action = f.payload as string[];
        break;
      case "accountId":
        if (f.filterValue) mapped.accountIds = [f.filterValue];
        break;
      case "type":
        mapped.entityType = f.payload as string[];
        break;
      case "time":
        const [start, end] = getDatesForTimeFilter(
          f.filterValue as AuditTimeFilter,
          f.payload as DateInterval
        );
        mapped.startDate = start;
        mapped.endDate = end;
        break;
      default:
        break;
    }
  });

  return mapped;
};
export const widgetFilterToSearchParams = (
  filters?: (WidgetFilter | FilterDefinition)[]
) => {
  const asSL: any = widgetFilterToSLFilter(filters);
  const timeFilter = filters?.find(
    ({ filterType }) => (filterType as FilterTypes) === "time"
  );
  if (timeFilter !== undefined) {
    asSL[TIME_FILTER_SEARCH_KEY] = encodeURI(timeFilter.filterValue);
  }
  if (decodeURI(asSL[TIME_FILTER_SEARCH_KEY]) !== "Custom time period") {
    delete asSL["startDate"];
    delete asSL["endDate"];
  }
  return objectToSearchParams(asSL);
};

const SLFilterToWidgetFilter = (
  slFilters: AuditFilters
): FilterDefinition[] => {
  const filters: FilterDefinition[] = [];
  if (slFilters.entityType !== undefined)
    filters.push({
      filterType: "type",
      filterValue: "",
      payload: slFilters.entityType,
    });
  if (slFilters.entityId !== undefined)
    filters.push({
      filterType: "entityId",
      filterValue: "",
      payload: slFilters.entityId,
    });
  if (slFilters.accountIds !== undefined && slFilters.accountIds.length > 0)
    filters.push({
      filterType: "accountId",
      filterValue: slFilters.accountIds[0],
    });
  if (slFilters.action !== undefined)
    filters.push({
      filterType: "action",
      filterValue: "",
      payload: slFilters.action,
    });
  if (slFilters.userName)
    filters.push({
      filterType: "email",
      filterValue: slFilters.userName,
    });
  if (slFilters["startDate"] && slFilters["endDate"])
    filters.push({
      filterType: "time",
      filterValue: "Custom time period",
      payload: { startDate: slFilters.startDate, endDate: slFilters.endDate },
    });

  return filters;
};

export const searchToSLFilter = (params: URLSearchParams): AuditFilters => {
  const filters: AuditFilters = {};
  asString(filters, params, ["userName"]);
  asNumber(filters, params, ["startDate", "endDate"]);

  asNonEmptyArray(filters, params, [
    "entityType",
    "entityId",
    "accountIds",
    "action",
  ]);
  return filters;
};
const searchToWidgetFilter = (params: URLSearchParams): FilterDefinition[] => {
  let definition = SLFilterToWidgetFilter(searchToSLFilter(params));
  let timeFilterType = decodeURI(params.get(TIME_FILTER_SEARCH_KEY) ?? "");
  if (timeFilterType === "") return definition;
  let timeFilter = definition.find((f) => f.filterType === "time");
  if (
    !definition.find((f) => {
      return f.filterType === "time";
    }) &&
    timeFilterType !== "Custom time period"
  ) {
    definition.push({ filterType: "time", filterValue: timeFilterType });
  } else {
    definition = definition.filter((f) => {
      return f.filterType !== "time";
    });

    definition.push({
      filterType: "time",
      filterValue: timeFilterType,
      payload:
        timeFilter && timeFilterType === "Custom time period"
          ? timeFilter.payload
          : undefined,
    });
  }
  return definition;
};
export default {
  fromDefinition: {
    toSL: (filters?: (WidgetFilter | FilterDefinition)[]) =>
      widgetFilterToSLFilter(filters),
    toSearchParams: (filters?: (WidgetFilter | FilterDefinition)[]) =>
      widgetFilterToSearchParams(filters),
  },
  fromSL: {
    toDefinition: (f: AuditFilters) => SLFilterToWidgetFilter(f),
    toSearchParams: (f: AuditFilters) => SLFilterToSearch(f),
  },
  fromSearchParams: {
    toSL: searchToSLFilter,
    toDefinition: (params: URLSearchParams) => searchToWidgetFilter(params),
  },
};
